import React from 'react'

import { Alert, Box, Button } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Box, F0Dialog, F0Text } from '@factorialco/f0-react'
import { EmployeeFoldersQuery } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import FolderSelector from 'modules/documents/components/Folders/FolderSelector'
import FolderSelectorF0 from 'modules/documents/components/Folders/FolderSelectorF0'
import { useFolderSelectorDialogFix } from './useFolderSelectorDialogFix'
import { type DocumentLike } from 'modules/documents/lib/Documents'
import useDocumentContextlessPolicies from 'modules/documents/hooks/useDocumentContextlessPolicies'
import { useGetEmployeeFolder } from 'modules/documents/hooks/useGetEmployeeFolder'
import { Folder } from 'modules/documents/hooks/useGetFolders'

type EmployeeFolder = EmployeeFoldersQuery['employees']['employees'][number]['folders'][number]

const getCommonElements = (arrays: string[][]): string[] | undefined => {
  if (arrays.length <= 1) return arrays.length === 1 ? arrays[0] : []

  const referenceArray = arrays[0]

  if (!referenceArray) return undefined

  const commonElements = referenceArray.filter((element) =>
    arrays.every((array) => array.includes(element))
  )

  return commonElements
}

const foldersInCommon = (employeeIds: string[], employeeFolders: EmployeeFolder[]) => {
  const processedEmployeeFoldersIds = employeeIds.map((employeeId) => {
    return employeeFolders
      .filter((employeeFolder) => employeeFolder.employeeId === employeeId)
      .map((employeeFolder) => employeeFolder.folderId)
  })

  const commonFoldersIds = getCommonElements(processedEmployeeFoldersIds)

  if (commonFoldersIds === undefined) return []

  const commonFoldersWithDuplicates = employeeFolders
    .filter((employeeFolder) => commonFoldersIds.includes(employeeFolder.folderId))
    .map((elem) => elem.folder)
    .filter(Boolean)

  return [...new Set(commonFoldersWithDuplicates)]
}

const extractEmployeeIdsFromDocuments = (documents: DocumentLike[]) => {
  return documents.map((doc) => doc?.employee?.id).filter(Boolean)
}

const defineInitialFolder = (docs: DocumentLike[], folders: Folder[]): Folder | undefined => {
  const folder = docs[0]?.folder

  if (folder) {
    return folders.find((elem) => elem.id === folder.id) || folders[0]
  }

  return undefined
}

interface Props {
  documents: DocumentLike[]
  loading?: boolean
  onSubmit: (selectedFolderId: string | null) => void
  onClose: () => void
  useF0?: boolean
}

interface F0ContentProps {
  documents: DocumentLike[]
  folders: Folder[]
  initialFolder: Folder | undefined
  canManageDefaultEmployeeFolder: boolean | undefined
  multiDocuments: boolean
  anyDestinationFolder: boolean
  setSelectedFolderId: (id: string | null) => void
}

const UpdateFolderModalF0Content = ({
  documents,
  folders,
  initialFolder,
  canManageDefaultEmployeeFolder,
  multiDocuments,
  anyDestinationFolder,
  setSelectedFolderId,
}: F0ContentProps) => {
  return (
    <F0Box display="flex" flexDirection="column" gap="xl">
      <F0Text
        variant="body"
        content={i18n.tp('documents.employee.bulk-actions.update-folder.description', {
          count: documents.length,
        })}
      />
      {anyDestinationFolder && folders.length > 0 && (
        <FolderSelectorF0
          folders={folders}
          initialFolder={initialFolder}
          onChange={setSelectedFolderId}
          noneOption={canManageDefaultEmployeeFolder ?? false}
        />
      )}
      {multiDocuments && (
        <F0Alert
          variant="warning"
          title={i18n.t('documents.employee.bulk-actions.update-folder.alert-datatable')}
          description=""
        />
      )}
    </F0Box>
  )
}

interface F0ModalProps {
  documents: DocumentLike[]
  folders: Folder[]
  initialFolder: Folder | undefined
  canManageDefaultEmployeeFolder: boolean | undefined
  multiDocuments: boolean
  anyDestinationFolder: boolean
  isLoading: boolean
  selectedFolderId: string | null
  setSelectedFolderId: (id: string | null) => void
  onSubmit: (selectedFolderId: string | null) => void
  onClose: () => void
  filename: string
}

const UpdateFolderModalF0 = ({
  documents,
  folders,
  initialFolder,
  canManageDefaultEmployeeFolder,
  multiDocuments,
  anyDestinationFolder,
  isLoading,
  selectedFolderId,
  setSelectedFolderId,
  onSubmit,
  onClose,
  filename,
}: F0ModalProps) => {
  const guardedOnClose = useFolderSelectorDialogFix(onClose)

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.tp('documents.employee.bulk-actions.update-folder.title', {
        count: documents.length,
        filename: filename,
      })}
      onClose={guardedOnClose}
      primaryAction={{
        label: i18n.t('documents.employee.bulk-actions.update-folder.submit'),
        onClick: () => onSubmit(selectedFolderId),
        disabled: !anyDestinationFolder,
        loading: isLoading,
      }}
      secondaryAction={{
        label: i18n.t('documents.employee.bulk-actions.update-folder.cancel'),
        onClick: onClose,
      }}
    >
      <UpdateFolderModalF0Content
        documents={documents}
        folders={folders}
        initialFolder={initialFolder}
        canManageDefaultEmployeeFolder={canManageDefaultEmployeeFolder}
        multiDocuments={multiDocuments}
        anyDestinationFolder={anyDestinationFolder}
        setSelectedFolderId={setSelectedFolderId}
      />
    </F0Dialog>
  )
}

const UpdateFolderModal = ({ documents, loading, onSubmit, onClose, useF0 = false }: Props) => {
  const { policies } = useDocumentContextlessPolicies()
  const [selectedFolderId, setSelectedFolderId] = React.useState<string | null>(null)

  const multiDocuments = useUnsafeMemo(() => {
    return documents.length > 1
  }, [documents])

  const employeeIds = useUnsafeMemo(() => {
    return extractEmployeeIdsFromDocuments(documents)
  }, [documents])

  const { employeeFolders, loading: loadingEmployeeFolder } = useGetEmployeeFolder(employeeIds)

  const editableEmployeeFolders = useUnsafeMemo(() => {
    return employeeFolders?.filter((employee) => employee._policies?.edit.can)
  }, [employeeFolders])

  const commonFolders = useUnsafeMemo(() => {
    return editableEmployeeFolders && employeeIds
      ? foldersInCommon(
          employeeIds,
          editableEmployeeFolders.filter((ef): ef is EmployeeFolder => !!ef)
        )
      : []
  }, [editableEmployeeFolders, employeeIds])

  const folders = useUnsafeMemo(() => {
    return commonFolders.filter((folder) => !!folder)
  }, [commonFolders])

  const initialFolder = useUnsafeMemo(() => {
    return folders ? defineInitialFolder(documents, folders) : undefined
  }, [folders, documents])

  const canManageDefaultEmployeeFolder = useUnsafeMemo(() => {
    return policies?.employeeManageDefaultEmployeeFolder.can
  }, [policies])

  const anyDestinationFolder = useUnsafeMemo(() => {
    return folders.length > 0 || canManageDefaultEmployeeFolder
  }, [folders, canManageDefaultEmployeeFolder])

  useUnsafeEffect(() => {
    if (initialFolder) setSelectedFolderId(String(initialFolder?.id))
  }, [initialFolder])

  const isLoading = useUnsafeMemo(() => {
    return loading || loadingEmployeeFolder
  }, [loading, loadingEmployeeFolder])

  const filename = useUnsafeMemo(() => {
    return documents[0] && documents.length > 0 ? documents[0].filename : ''
  }, [documents])

  if (useF0) {
    if (!employeeFolders) return null

    return (
      <UpdateFolderModalF0
        documents={documents}
        folders={folders}
        initialFolder={initialFolder}
        canManageDefaultEmployeeFolder={canManageDefaultEmployeeFolder}
        multiDocuments={multiDocuments}
        anyDestinationFolder={anyDestinationFolder ?? false}
        isLoading={isLoading}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        onSubmit={onSubmit}
        onClose={onClose}
        filename={filename ?? ''}
      />
    )
  }

  return (
    <>
      {employeeFolders && (
        <Modal
          illustration="file-move"
          title={i18n.tp('documents.employee.bulk-actions.update-folder.title', {
            count: documents.length,
            filename: filename,
          })}
          description={i18n.tp('documents.employee.bulk-actions.update-folder.description', {
            count: documents.length,
          })}
          onSubmit={() => onSubmit(selectedFolderId)}
          onClose={onClose}
          footer={
            <>
              <Button type="secondary" onClick={onClose}>
                {i18n.t('documents.employee.bulk-actions.update-folder.cancel')}
              </Button>
              <Button
                submit
                iconName={isLoading ? 'folder' : undefined}
                loading={isLoading}
                disabled={!anyDestinationFolder}
              >
                {i18n.t('documents.employee.bulk-actions.update-folder.submit')}
              </Button>
            </>
          }
        >
          <Box gap="s16">
            {anyDestinationFolder && (
              <FolderSelector
                folders={folders}
                initialFolder={initialFolder}
                label={i18n.t('split_documents.assign_documents.destination_folder')}
                onChange={setSelectedFolderId}
                noneOption={canManageDefaultEmployeeFolder ?? false}
                aria-label={i18n.t('documents.employee.bulk-actions.update-folder.selector')}
              />
            )}
            {multiDocuments && (
              <Alert color="warning">
                {i18n.t('documents.employee.bulk-actions.update-folder.alert-datatable')}
              </Alert>
            )}
          </Box>
        </Modal>
      )}
    </>
  )
}

export default UpdateFolderModal
